import { z } from 'zod';

const StoredPolynomicClient = z.object({
  clientId: z.string(),
  observedAt: z.string(),
});

export type PolynomicClient = z.infer<typeof StoredPolynomicClient>;

const STORAGE_KEY = 'polynomic_client';
const CLIENT_ID = /^[A-Za-z0-9_-]{1,200}$/;
const TIME_ZONE = /(Z|[+-]\d{2}(:?\d{2})?)$/i;
const MAX_READ_AGE_MS = 24 * 60 * 60 * 1000;
const MAX_CLOCK_SKEW_MS = 5 * 60 * 1000;

function validPolynomicClient(
  clientId: string,
  observedAt: string,
  nowMs: number
): PolynomicClient | undefined {
  const observedMs = Date.parse(observedAt);
  if (
    !CLIENT_ID.test(clientId) ||
    !TIME_ZONE.test(observedAt) ||
    Number.isNaN(observedMs) ||
    observedMs < nowMs - MAX_READ_AGE_MS ||
    observedMs > nowMs + MAX_CLOCK_SKEW_MS
  ) {
    return undefined;
  }
  return { clientId, observedAt };
}

export function capturePolynomicClient(win: Window = window): void {
  const url = new URL(win.location.href);
  const clientId = url.searchParams.get('pcid');
  const observedAt = url.searchParams.get('pcid_at');
  if (clientId === null && observedAt === null) return;
  url.searchParams.delete('pcid');
  url.searchParams.delete('pcid_at');
  win.history.replaceState(win.history.state, '', url.href);
  const client = validPolynomicClient(
    clientId ?? '',
    observedAt ?? '',
    Date.now()
  );
  if (client !== undefined) {
    win.localStorage.setItem(STORAGE_KEY, JSON.stringify(client));
  }
}

export function takePolynomicClient(
  win: Window = window
): PolynomicClient | undefined {
  const stored = win.localStorage.getItem(STORAGE_KEY);
  win.localStorage.removeItem(STORAGE_KEY);
  if (stored === null) return undefined;
  const parsed = StoredPolynomicClient.safeParse(safeJsonParse(stored));
  return parsed.success
    ? validPolynomicClient(
        parsed.data.clientId,
        parsed.data.observedAt,
        Date.now()
      )
    : undefined;
}

function safeJsonParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}
